<template>
  <Teleport to="body">
    <div
      v-if="show"
      ref="pillRef"
      class="minimized-call-pill"
      :class="{ 'is-dragging': isDragging }"
      :style="pillStyle"
      @mousedown="startDrag"
      @touchstart="startDrag"
    >
      <!-- Avatar -->
      <div class="pill-avatar">
        <span class="avatar-initials">{{ callerInitials }}</span>
      </div>

      <!-- Caller Info -->
      <div
        class="pill-info"
        @click="callState === 'active' ? toggleTranscription() : handleMaximize()"
      >
        <span class="pill-name">{{ callerName || formattedPhoneNumber }}</span>
        <span v-if="callState === 'ringing'" class="pill-status ringing">Ringing...</span>
        <span v-else class="pill-duration">{{ formattedCallDuration }}</span>
      </div>

      <button
        v-if="callState === 'active'"
        class="pill-btn transcription-btn"
        :class="{ active: showTranscription }"
        @click.stop="toggleTranscription"
        title="Toggle transcription"
      >
        <Icon icon="mdi:subtitles-outline" width="16" height="16" />
      </button>

      <button
        v-if="callState === 'ringing'"
        class="pill-btn decline-btn"
        @click.stop="handleDecline"
        title="Decline"
      >
        <Icon icon="mdi:phone-hangup" width="16" height="16" />
      </button>

      <button
        v-if="callState === 'ringing'"
        class="pill-btn answer-btn"
        @click.stop="handleAnswer"
        title="Answer"
      >
        <Icon icon="mdi:phone" width="16" height="16" />
      </button>

      <button
        v-if="callState === 'active'"
        class="pill-btn hangup-btn"
        @click.stop="handleHangup"
        title="Hangup"
      >
        <Icon icon="mdi:phone-hangup" width="16" height="16" />
      </button>

      <button class="pill-btn maximize-btn" @click.stop="handleMaximize" title="Maximize">
        <Icon icon="mdi:arrow-expand" width="16" height="16" />
      </button>

      <!-- Transcription Tooltip -->
      <transition name="tooltip-fade">
        <div
          v-if="showTranscription"
          class="transcription-tooltip"
          :class="{ 'tooltip-left': tooltipOnLeft }"
          @click.stop
        >
          <div class="tooltip-header">
            <span class="tooltip-title">
              <Icon icon="mdi:subtitles-outline" width="14" height="14" />
              Live Transcription
            </span>
            <button class="tooltip-close" @click="showTranscription = false">
              <Icon icon="mdi:close" width="14" height="14" />
            </button>
          </div>
          <div class="tooltip-content" ref="tooltipContentRef">
            <div
              v-for="(message, index) in transcriptionMessages"
              :key="`${message.id}-${index}`"
              class="transcription-message"
              :class="message.role?.toLowerCase().replace(' ', '-')"
            >
              <span class="speaker-label">{{ message.role }}:</span>
              <span class="message-text">{{ message.text }}</span>
            </div>
            <div v-if="transcriptionMessages.length === 0" class="no-transcription">
              Waiting for transcription...
            </div>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount, nextTick } from "vue";
import { Icon } from "@iconify/vue";

interface TranscriptionMessage {
  id: string | number;
  role: string;
  text: string;
}

interface Props {
  show: boolean;
  callerName?: string;
  callerPhoneNumber?: string;
  callDuration?: number;
  callState?: "ringing" | "active" | null;
  transcriptionMessages?: TranscriptionMessage[];
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  callerName: "",
  callerPhoneNumber: "",
  callDuration: 0,
  callState: null,
  transcriptionMessages: () => [],
});

const emit = defineEmits<{
  answer: [];
  decline: [];
  hangup: [];
  maximize: [];
}>();

const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const pillPosition = ref({ x: window.innerWidth - 380, y: window.innerHeight - 80 });
const pillRef = ref<HTMLElement | null>(null);
const showTranscription = ref(false);
const tooltipContentRef = ref<HTMLElement | null>(null);

const pillStyle = computed(() => ({
  left: `${pillPosition.value.x}px`,
  top: `${pillPosition.value.y}px`,
}));

// Check if tooltip would go off right edge of screen
const tooltipOnLeft = computed(() => {
  const pillWidth = 280; // min-width of pill
  const tooltipWidth = 320;
  const gap = 12;
  const padding = 10;
  // If pill is positioned such that tooltip on right would overflow, show on left
  return pillPosition.value.x + pillWidth + gap + tooltipWidth + padding > window.innerWidth;
});

const callerInitials = computed(() => {
  if (props.callerName) {
    const names = props.callerName.trim().split(/\s+/);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  }
  if (props.callerPhoneNumber) {
    const digits = props.callerPhoneNumber.replace(/\D/g, "");
    return digits.slice(-2) || "??";
  }
  return "??";
});

const formattedPhoneNumber = computed(() => {
  if (!props.callerPhoneNumber) return "Unknown";
  const cleaned = props.callerPhoneNumber.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return props.callerPhoneNumber;
});

const formattedCallDuration = computed(() => {
  if (props.callDuration > 0) {
    const hours = Math.floor(props.callDuration / 3600);
    const minutes = Math.floor((props.callDuration % 3600) / 60);
    const seconds = props.callDuration % 60;
    if (hours > 0) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return "00:00";
});

const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  // Don't drag when clicking buttons
  if (target.closest(".pill-btn") || target.closest(".transcription-tooltip")) {
    return;
  }

  event.preventDefault();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  dragStart.value = {
    x: clientX - pillPosition.value.x,
    y: clientY - pillPosition.value.y,
  };

  document.addEventListener("mousemove", handleDrag, { passive: false });
  document.addEventListener("mouseup", stopDrag);
  document.addEventListener("touchmove", handleDrag, { passive: false });
  document.addEventListener("touchend", stopDrag);
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const pill = pillRef.value;
  if (pill) {
    const rect = pill.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  pillPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
};

const toggleTranscription = () => {
  showTranscription.value = !showTranscription.value;
};

const handleAnswer = () => {
  emit("answer");
};

const handleDecline = () => {
  emit("decline");
};

const handleHangup = () => {
  emit("hangup");
};

const handleMaximize = () => {
  emit("maximize");
};

// Auto-scroll transcription tooltip to bottom when new messages arrive
watch(
  () => props.transcriptionMessages,
  async () => {
    if (showTranscription.value && tooltipContentRef.value) {
      await nextTick();
      tooltipContentRef.value.scrollTop = tooltipContentRef.value.scrollHeight;
    }
  },
  { deep: true }
);

// Reset position when showing
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      pillPosition.value = { x: window.innerWidth - 380, y: window.innerHeight - 80 };
    }
  }
);

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
});
</script>

<style scoped>
.minimized-call-pill {
  position: fixed;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-radius: 50px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 10001;
  cursor: grab;
  user-select: none;
  min-width: 280px;
  transition: box-shadow 0.2s ease;
}

.minimized-call-pill:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.minimized-call-pill.is-dragging {
  cursor: grabbing;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
}

.pill-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-initials {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
}

.pill-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.pill-name {
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pill-duration {
  font-size: 11px;
  color: #9ca3af;
  font-variant-numeric: tabular-nums;
}

.pill-status.ringing {
  font-size: 11px;
  color: #10b981;
  animation: pulse-text 1.5s ease-in-out infinite;
}

@keyframes pulse-text {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.pill-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.transcription-btn {
  background: #4b5563;
  color: #9ca3af;
}

.transcription-btn:hover {
  background: #6b7280;
  color: #ffffff;
}

.transcription-btn.active {
  background: #3e3efe;
  color: #ffffff;
}

.decline-btn,
.hangup-btn {
  background: #ef4444;
  color: #ffffff;
}

.decline-btn:hover,
.hangup-btn:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.answer-btn {
  background: #10b981;
  color: #ffffff;
}

.answer-btn:hover {
  background: #059669;
  transform: scale(1.1);
}

.maximize-btn {
  background: #4b5563;
  color: #ffffff;
}

.maximize-btn:hover {
  background: #6b7280;
  transform: scale(1.1);
}

/* Transcription Tooltip */
.transcription-tooltip {
  position: absolute;
  top: 50%;
  left: calc(100% + 12px);
  transform: translateY(-50%);
  width: 320px;
  max-height: 250px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  cursor: default;
}

/* Position tooltip on left when near right edge of screen */
.transcription-tooltip.tooltip-left {
  left: auto;
  right: calc(100% + 12px);
}

.tooltip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.tooltip-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.tooltip-close {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.tooltip-close:hover {
  background: #e5e7eb;
  color: #374151;
}

.tooltip-content {
  padding: 12px;
  max-height: 180px;
  overflow-y: auto;
}

.transcription-message {
  font-size: 11px;
  line-height: 1.5;
  padding: 6px 8px;
  background: #fafafa;
  border-radius: 6px;
  border-left: 3px solid transparent;
  margin-bottom: 6px;
}

.transcription-message:last-child {
  margin-bottom: 0;
}

.transcription-message.customer {
  border-left-color: #0066cc;
}

.transcription-message.sales-rep {
  border-left-color: #cc6600;
}

.speaker-label {
  font-weight: 600;
  color: #2c2c2e;
  margin-right: 6px;
}

.transcription-message.customer .speaker-label {
  color: #0066cc;
}

.transcription-message.sales-rep .speaker-label {
  color: #cc6600;
}

.message-text {
  color: #2c2c2e;
}

.no-transcription {
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  padding: 20px;
}

/* Tooltip scrollbar */
.tooltip-content::-webkit-scrollbar {
  width: 6px;
}

.tooltip-content::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.tooltip-content::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.tooltip-content::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}

/* Transitions */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: all 0.2s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
